/**
 * API Alignment Test
 * Tests to verify frontend-backend API alignment
 */

/* global console, Office */

import { chatAPI, healthAPI } from "./api.js";
import { msProjectService } from "./msProjectService.js";

export class APIAlignmentTest {
  constructor() {
    // No need for MS Project service - we'll use Office.js directly
    this.testResults = [];
  }

  /**
   * Get project data using MSProjectService with health checks
   * @returns {Promise<Object>} Project data
   */
  async getProjectData() {
    try {
      console.log("APIAlignmentTest: getProjectData() starting...");

      // First check if we have real project data loaded
      let realTasks = [];
      if (window.allProjectTasks && Array.isArray(window.allProjectTasks)) {
        realTasks = window.allProjectTasks;
        console.log(`Using real project data: ${realTasks.length} tasks loaded`);
      } else {
        console.log("APIAlignmentTest: No real project tasks found in window.allProjectTasks");
      }

      // If we have real data, return it immediately
      if (realTasks.length > 0) {
        console.log("APIAlignmentTest: Returning real project data immediately");
        const result = {
          project_id: `ms_project_${Date.now()}`,
          fileInfo: { name: "Project Data" },
          properties: { name: "Project Data" },
          tasks: realTasks,
          assignments: [],
          resources: [],
          metadata: {
            extractedAt: new Date().toISOString(),
            dataSource: "real_project_data",
            tasksCount: realTasks.length,
          },
        };

        console.log("APIAlignmentTest: getProjectData() completed successfully:", {
          projectId: result.project_id,
          tasksCount: result.tasks.length,
          dataSource: result.metadata.dataSource,
        });

        return result;
      }

      // Fallback: Use MSProjectService for comprehensive data
      console.log("APIAlignmentTest: No real data available, using MSProjectService...");
      const projectData = await msProjectService.getProjectData();
      
      console.log("APIAlignmentTest: getProjectData() completed successfully:", {
        projectId: projectData.project_id,
        tasks: projectData.tasks.length,
        assignments: projectData.assignments.length,
        fileInfo: projectData.fileInfo
      });

      return projectData;
      } catch (error) {
      console.error("APIAlignmentTest: getProjectData() failed:", error);
      
      // Even in case of error, try to return real data if available
      let fallbackTasks = [];
      if (window.allProjectTasks && Array.isArray(window.allProjectTasks)) {
        fallbackTasks = window.allProjectTasks;
      }

      return {
        project_id: `ms_project_${Date.now()}`,
        fileInfo: { name: "Unknown Project" },
        properties: { name: "Unknown Project" },
        tasks: fallbackTasks,
        assignments: [],
        resources: [],
        metadata: {
          extractedAt: new Date().toISOString(),
          error: error.message,
          dataSource: fallbackTasks.length > 0 ? "real_project_data_fallback" : "error_state",
          tasksCount: fallbackTasks.length,
        },
        retrieved_at: new Date().toISOString(),
      };
    }
  }

  /**
   * Get task by index using MSProjectService
   * @param {number} taskIndex - Task index (1-based)
   * @returns {Promise<Object|null>} Task data
   */
  async getTaskByIndex(taskIndex) {
    try {
      console.log(`APIAlignmentTest: getTaskByIndex(${taskIndex}) starting...`);

      // Use MSProjectService to get task data
      const task = await msProjectService.getTaskByIndex(taskIndex);
      
      if (task) {
        console.log(`APIAlignmentTest: getTaskByIndex(${taskIndex}) completed successfully:`, {
          id: task.id,
          name: task.name,
          start: task.start,
          finish: task.finish
        });
        } else {
        console.log(`APIAlignmentTest: getTaskByIndex(${taskIndex}) - no task found`);
      }

      return task;
    } catch (error) {
      console.error(`APIAlignmentTest: getTaskByIndex(${taskIndex}) failed:`, error);
      return null;
    }
  }

  /**
   * Get resource by index using Office.js directly
   * @param {number} resourceIndex - Resource index (1-based)
   * @returns {Promise<Object|null>} Resource data
   */
  async getResourceByIndex(resourceIndex) {
    try {
      if (!Office || !Office.context || !Office.context.document) {
        return null;
      }

      const document = Office.context.document;
      const resourceData = {};
      let completedFields = 0;
      const totalFields = 25; // Increased for more fields

      const checkCompletion = () => {
        completedFields++;
        if (completedFields === totalFields) {
          const resource = {
            id: resourceData.ID || resourceIndex,
            name: resourceData.Name || `Resource ${resourceIndex}`,
            type: resourceData.Type || "Work",
            cost: resourceData.Cost || 0,
            work: resourceData.Work || 0,
            units: resourceData.Units || 100,
            accrual: resourceData.Accrual || 3,
            actualCost: resourceData.ActualCost || 0,
            actualWork: resourceData.ActualWork || 0,
            baselineCost: resourceData.BaselineCost || 0,
            baselineWork: resourceData.BaselineWork || 0,
            budgetCost: resourceData.BudgetCost || 0,
            budgetWork: resourceData.BudgetWork || 0,
            code: resourceData.Code || "",
            maxUnits: resourceData.MaxUnits || 100,
            overAllocated: resourceData.OverAllocated || false,
            overtimeCost: resourceData.OvertimeCost || 0,
            overtimeWork: resourceData.OvertimeWork || 0,
            percentWorkComplete: resourceData.PercentWorkComplete || 0,
            regularWork: resourceData.RegularWork || 0,
            remainingCost: resourceData.RemainingCost || 0,
            remainingWork: resourceData.RemainingWork || 0,
            start: resourceData.Start ? new Date(resourceData.Start).toISOString() : null,
            retrieved_at: new Date().toISOString(),
          };
          return resource;
        }
      };

      // Get Name field
      document.getResourceFieldAsync(resourceIndex, Office.ProjectResourceFields.Name, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resourceData.Name = result.value;
        } else {
          resourceData.Name = `Resource ${resourceIndex}`;
        }
        checkCompletion();
      });

      // Get ID field
      document.getResourceFieldAsync(resourceIndex, Office.ProjectResourceFields.ID, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resourceData.ID = result.value;
        } else {
          resourceData.ID = resourceIndex;
        }
        checkCompletion();
      });

      // Get Type field
      document.getResourceFieldAsync(resourceIndex, Office.ProjectResourceFields.Type, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resourceData.Type = result.value;
        } else {
          resourceData.Type = "Work";
        }
        checkCompletion();
      });

      // Get Cost field
      document.getResourceFieldAsync(resourceIndex, Office.ProjectResourceFields.Cost, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resourceData.Cost = result.value;
        } else {
          resourceData.Cost = 0;
        }
        checkCompletion();
      });

      // Get Work field
      document.getResourceFieldAsync(resourceIndex, Office.ProjectResourceFields.Work, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resourceData.Work = result.value;
        } else {
          resourceData.Work = 0;
        }
        checkCompletion();
      });

      // Get Units field
      document.getResourceFieldAsync(
        resourceIndex,
        Office.ProjectResourceFields.Units,
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resourceData.Units = result.value;
          } else {
            resourceData.Units = 100;
          }
          checkCompletion();
        }
      );

      // Get Accrual field
      document.getResourceFieldAsync(
        resourceIndex,
        Office.ProjectResourceFields.Accrual,
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resourceData.Accrual = result.value;
          } else {
            resourceData.Accrual = 3; // Default: Prorated
          }
          checkCompletion();
        }
      );

      // Get ActualCost field
      document.getResourceFieldAsync(
        resourceIndex,
        Office.ProjectResourceFields.ActualCost,
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resourceData.ActualCost = result.value;
          } else {
            resourceData.ActualCost = 0;
          }
          checkCompletion();
        }
      );

      // Get ActualWork field
      document.getResourceFieldAsync(
        resourceIndex,
        Office.ProjectResourceFields.ActualWork,
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resourceData.ActualWork = result.value;
          } else {
            resourceData.ActualWork = 0;
          }
          checkCompletion();
        }
      );

      // Get BaselineCost field
      document.getResourceFieldAsync(
        resourceIndex,
        Office.ProjectResourceFields.BaselineCost,
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resourceData.BaselineCost = result.value;
          } else {
            resourceData.BaselineCost = 0;
          }
          checkCompletion();
        }
      );

      // Get BaselineWork field
      document.getResourceFieldAsync(
        resourceIndex,
        Office.ProjectResourceFields.BaselineWork,
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resourceData.BaselineWork = result.value;
          } else {
            resourceData.BaselineWork = 0;
          }
          checkCompletion();
        }
      );

      // Get BudgetCost field
      document.getResourceFieldAsync(
        resourceIndex,
        Office.ProjectResourceFields.BudgetCost,
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resourceData.BudgetCost = result.value;
          } else {
            resourceData.BudgetCost = 0;
          }
          checkCompletion();
        }
      );

      // Get BudgetWork field
      document.getResourceFieldAsync(
        resourceIndex,
        Office.ProjectResourceFields.BudgetWork,
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resourceData.BudgetWork = result.value;
          } else {
            resourceData.BudgetWork = 0;
          }
          checkCompletion();
        }
      );

      // Get Code field
      document.getResourceFieldAsync(resourceIndex, Office.ProjectResourceFields.Code, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resourceData.Code = result.value;
        } else {
          resourceData.Code = "";
        }
        checkCompletion();
      });

      // Get MaxUnits field
      document.getResourceFieldAsync(
        resourceIndex,
        Office.ProjectResourceFields.MaxUnits,
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resourceData.MaxUnits = result.value;
          } else {
            resourceData.MaxUnits = 100;
          }
          checkCompletion();
        }
      );

      // Get OverAllocated field
      document.getResourceFieldAsync(
        resourceIndex,
        Office.ProjectResourceFields.OverAllocated,
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resourceData.OverAllocated = result.value;
          } else {
            resourceData.OverAllocated = false;
          }
          checkCompletion();
        }
      );

      // Get OvertimeCost field
      document.getResourceFieldAsync(
        resourceIndex,
        Office.ProjectResourceFields.OvertimeCost,
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resourceData.OvertimeCost = result.value;
          } else {
            resourceData.OvertimeCost = 0;
          }
          checkCompletion();
        }
      );

      // Get OvertimeWork field
      document.getResourceFieldAsync(
        resourceIndex,
        Office.ProjectResourceFields.OvertimeWork,
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resourceData.OvertimeWork = result.value;
          } else {
            resourceData.OvertimeWork = 0;
          }
          checkCompletion();
        }
      );

      // Get PercentWorkComplete field
      document.getResourceFieldAsync(
        resourceIndex,
        Office.ProjectResourceFields.PercentWorkComplete,
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resourceData.PercentWorkComplete = result.value;
          } else {
            resourceData.PercentWorkComplete = 0;
          }
          checkCompletion();
        }
      );

      // Get RegularWork field
      document.getResourceFieldAsync(
        resourceIndex,
        Office.ProjectResourceFields.RegularWork,
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resourceData.RegularWork = result.value;
          } else {
            resourceData.RegularWork = 0;
          }
          checkCompletion();
        }
      );

      // Get RemainingCost field
      document.getResourceFieldAsync(
        resourceIndex,
        Office.ProjectResourceFields.RemainingCost,
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resourceData.RemainingCost = result.value;
          } else {
            resourceData.RemainingCost = 0;
          }
          checkCompletion();
        }
      );

      // Get RemainingWork field
      document.getResourceFieldAsync(
        resourceIndex,
        Office.ProjectResourceFields.RemainingWork,
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resourceData.RemainingWork = result.value;
          } else {
            resourceData.RemainingWork = 0;
          }
          checkCompletion();
        }
      );

      // Get Start field
      document.getResourceFieldAsync(
        resourceIndex,
        Office.ProjectResourceFields.Start,
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resourceData.Start = result.value;
          } else {
            resourceData.Start = null;
          }
          checkCompletion();
        }
      );

      // Return a promise that resolves when all fields are retrieved
      return new Promise((resolve) => {
        const originalCheckCompletion = checkCompletion;
        const checkCompletion = () => {
          const result = originalCheckCompletion();
          if (result) {
            resolve(result);
          }
        };
      });
    } catch (error) {
      console.error(`Error getting resource ${resourceIndex}:`, error);
      return {
        id: resourceIndex,
        name: `Resource ${resourceIndex}`,
        type: "Work",
        cost: 0,
        work: 0,
        units: 100,
        retrieved_at: new Date().toISOString(),
      };
    }
  }


  /**
   * Test health check endpoint
   */
  async testHealthCheck() {
    try {
      const healthResponse = await healthAPI.checkHealth();

      // Verify expected fields
      const expectedFields = [
        "status",
        "timestamp",
        "version",
        "database_status",
        "anthropic_status",
      ];
      const hasAllFields = expectedFields.every((field) => field in healthResponse);

      this.testResults.push({
        test: "health_check",
        success: hasAllFields,
        details: {
          response: healthResponse,
          missingFields: expectedFields.filter((field) => !(field in healthResponse)),
        },
      });
    } catch (error) {
      this.testResults.push({
        test: "health_check",
        success: false,
        error: error.message,
      });
      console.error("Health check test failed:", error);
    }
  }

  /**
   * Test session creation
   */
  async testSessionCreation() {
    try {
      const sessionData = {
        title: "API Alignment Test Session",
        user_id: "test_user_" + Date.now(),
        project_context: {
          current_project: {
            project_id: "test_project",
            project_name: "Test Project",
            tasks: [],
            resources: [],
            metadata: { source: "test" },
          },
        },
      };

      const sessionResponse = await chatAPI.createSession(sessionData);

      // Verify expected fields
      const expectedFields = ["id", "session_id", "user_id", "title", "status", "created_at"];
      const hasAllFields = expectedFields.every((field) => field in sessionResponse);

      this.testResults.push({
        test: "session_creation",
        success: hasAllFields,
        details: {
          response: sessionResponse,
          missingFields: expectedFields.filter((field) => !(field in sessionResponse)),
        },
        sessionId: sessionResponse.session_id,
      });
    } catch (error) {
      this.testResults.push({
        test: "session_creation",
        success: false,
        error: error.message,
      });
      console.error("Session creation test failed:", error);
    }
  }

  /**
   * Test message sending (non-streaming)
   */
  async testMessageSending() {
    try {
      const messageData = {
        message: "Hello, this is a test message",
        session_id: this.getLastSessionId(),
        user_id: "test_user_" + Date.now(),
        project_context: {
          current_project: {
            project_id: "test_project",
            project_name: "Test Project",
            tasks: [{ id: 1, name: "Test Task", duration: 5, percent_complete: 50 }],
            resources: [{ id: 1, name: "Test Resource", type: "Work" }],
            metadata: { source: "test" },
          },
        },
      };

      const messageResponse = await chatAPI.sendMessage(messageData);

      // Verify expected fields
      const expectedFields = ["message", "session_id", "message_id", "role", "created_at"];
      const hasAllFields = expectedFields.every((field) => field in messageResponse);

      this.testResults.push({
        test: "message_sending",
        success: hasAllFields,
        details: {
          response: messageResponse,
          missingFields: expectedFields.filter((field) => !(field in messageResponse)),
        },
      });
    } catch (error) {
      this.testResults.push({
        test: "message_sending",
        success: false,
        error: error.message,
      });
      console.error("Message sending test failed:", error);
    }
  }

  /**
   * Test streaming message
   */
  async testStreamingMessage() {
    try {
      const messageData = {
        message: "Hello, this is a streaming test message",
        session_id: this.getLastSessionId(),
        user_id: "test_user_" + Date.now(),
        stream: true,
      };

      let chunksReceived = 0;
      let finalResponse = null;
      let error = null;

      await new Promise((resolve, reject) => {
        chatAPI.sendMessageStream(
          messageData,
          () => {
            chunksReceived++;
          },
          (completeResponse) => {
            finalResponse = completeResponse;
            resolve();
          },
          (streamError) => {
            error = streamError;
            console.error("Streaming error:", streamError);
            reject(streamError);
          }
        );
      });

      const success = chunksReceived > 0 && finalResponse && !error;

      this.testResults.push({
        test: "streaming_message",
        success: success,
        details: {
          chunksReceived,
          finalResponse,
          error,
        },
      });
    } catch (error) {
      this.testResults.push({
        test: "streaming_message",
        success: false,
        error: error.message,
      });
      console.error("Streaming message test failed:", error);
    }
  }

  /**
   * Test project context format
   */
  async testProjectContextFormat() {
    try {
      // Test with direct Office.js calls
      const projectData = await this.getProjectData();

      // Verify project data structure
      const expectedFields = [
        "project_id",
        "fileInfo",
        "properties",
        "tasks",
        "resources",
        "assignments",
        "metadata",
      ];
      const hasAllFields = expectedFields.every((field) => field in projectData);

      this.testResults.push({
        test: "project_context_format",
        success: hasAllFields,
        details: {
          projectData: projectData,
          missingFields: expectedFields.filter((field) => !(field in projectData)),
        },
      });
    } catch (error) {
      this.testResults.push({
        test: "project_context_format",
        success: false,
        error: error.message,
        note: "This test requires MS Project environment",
      });
      console.warn(
        "Project context format test failed (expected in non-MS Project environment):",
        error.message
      );
    }
  }

  /**
   * Get the last session ID from test results
   */
  getLastSessionId() {
    const sessionTest = this.testResults.find(
      (result) => result.test === "session_creation" && result.sessionId
    );
    return sessionTest ? sessionTest.sessionId : null;
  }

  /**
   * Generate test summary
   */
  generateSummary() {
    const totalTests = this.testResults.length;
    const passedTests = this.testResults.filter((result) => result.success).length;
    const failedTests = totalTests - passedTests;

    return {
      total: totalTests,
      passed: passedTests,
      failed: failedTests,
      successRate: totalTests > 0 ? ((passedTests / totalTests) * 100).toFixed(1) + "%" : "0%",
    };
  }
}

// Export for use in other modules
export default APIAlignmentTest;
